<script lang="ts">
	import { getCopy } from '$lib/lang/copy.svelte';
	import Tooltip from '$lib/components/fragments/Tooltip.svelte';
	import { md } from '$lib/global.svelte';

	import {
		scaleLinear,
		scaleOrdinal,
		extent,
		stack,
		stackOrderNone,
		stackOffsetNone,
		area
	} from 'd3';

	import data from '$lib/data/ncd.csv';

	const copy = $derived(getCopy().global_shift.ncd_chart);

	let hovered = $state(false);
	let hoveredSection = $state<string | null>(null);
	let tooltip = $state<{ name: string; year: number; value: number } | null>(null);

	let width = $state(0);

	const margin = $derived({
		top: 6,
		right: md.current ? 0 : 5,
		bottom: 60,
		left: md.current ? 70 : 40
	});

	const height = $derived((md.current ? 650 : 500) - margin.top - margin.bottom);

	const years = $derived(
		Object.keys(data[0])
			.filter((k) => k !== 'disease_name')
			.map(Number)
	);

	const groupedDiseases = ['NCDs', 'CDs', 'Injuries'];

	const groupedData = $derived(
		years.map((year) => {
			const sums = { Injuries: 0, CDs: 0, NCDs: 0 };

			for (const d of data) {
				const value = +d[year];
				if (d.disease_name === 'Injuries') sums.Injuries += value;
				else if (d.disease_name === 'CDs') sums.CDs += value;
				else sums.NCDs += value;
			}

			const total = sums.Injuries + sums.CDs + sums.NCDs;
			return {
				year,
				Injuries: sums.Injuries / total,
				CDs: sums.CDs / total,
				NCDs: sums.NCDs / total
			};
		})
	);

	const fullData = $derived(
		years.map((year) => {
			const row: Record<string, number | string> = { year };
			const rawValues = data.map((d) => ({
				name: d.disease_name,
				value: +d[year]
			}));
			const total = rawValues.reduce((sum, d) => sum + d.value, 0);
			rawValues.forEach((d) => {
				row[d.name] = d.value / total;
			});
			return row;
		})
	);

	const notNCDs = (d: string) => d === 'Injuries' || d === 'CDs';

	const diseaseTotals = data
		.map((d) => ({
			name: d.disease_name,
			total: years.reduce((sum, y) => sum + +d[y], 0)
		}))
		.filter((d) => !notNCDs(d.name))
		.sort((a, b) => b.total - a.total)
		.map((d) => d.name);

	const fullDiseases = [...diseaseTotals, 'CDs', 'Injuries'];

	const groupedStack = $derived(
		stack().keys(groupedDiseases).order(stackOrderNone).offset(stackOffsetNone)(groupedData as any)
	);

	const fullStack = $derived(
		stack().keys(fullDiseases).order(stackOrderNone).offset(stackOffsetNone)(fullData as any)
	);

	const xScale = $derived(
		scaleLinear()
			.domain(extent(years) as [number, number])
			.range([0, width - margin.left - margin.right])
	);

	const yScale = $derived(scaleLinear().domain([0, 1]).range([height, 0]));

	const color = scaleOrdinal<string, string>()
		.domain([...fullDiseases, 'NCDs'])
		.range(
			[...fullDiseases, 'NCDs'].map((name) => {
				if (name === 'Injuries') return '#0E1F8D';
				if (name === 'CDs') return '#0E1E56';
				return '#FF3C4C';
			})
		);

	const handleMouseEnter = () => {
		hovered = true;
	};

	const handleMouseLeave = () => {
		hovered = false;
		tooltip = null;
		hoveredSection = null;
	};
</script>

<div class="relative w-full" bind:clientWidth={width}>
	<svg
		{width}
		height={height + margin.top + margin.bottom}
		role="figure"
		aria-label="Hover to see breakdown of noncommunicable diseases"
		class="cursor-crosshair"
		onmouseenter={handleMouseEnter}
		onmouseleave={handleMouseLeave}
		onmousemove={(e) => {
			const index = years.findIndex(
				(y) => y === Math.round(xScale.invert(e.offsetX - margin.left))
			);
			const year = years[index];

			const hoveredLayer = fullStack.find((layer) => {
				const [y0, y1] = layer[index] ?? [];
				return yScale(y1) <= e.offsetY && e.offsetY <= yScale(y0);
			});

			if (hoveredLayer && !['Injuries', 'CDs'].includes(hoveredLayer.key)) {
				const value = fullData[index]?.[hoveredLayer.key] as number;
				tooltip = {
					name: copy.labels[hoveredLayer.key],
					year,
					value: Math.round(value * 100)
				};
				hoveredSection = hoveredLayer.key;
			} else {
				tooltip = null;
				hoveredSection = null;
			}
		}}
	>
		<g transform="translate({margin.left}, {margin.top})">
			{#if hovered}
				{#each fullStack as layer}
					<path
						d={area<any>()
							.x((d, i) => xScale(+fullData[i].year))
							.y0((d) => yScale(d[0]))
							.y1((d) => yScale(d[1]))(layer)}
						fill={color(layer.key)}
						stroke="white"
						stroke-width="0.5"
						opacity={layer.key === 'Injuries' || layer.key === 'CDs'
							? 0.2
							: hoveredSection
								? layer.key === hoveredSection
									? 1
									: 0.85
								: 1}
					/>
				{/each}

				{#if tooltip?.year !== undefined}
					<line
						x1={xScale(+tooltip.year)}
						x2={xScale(+tooltip.year)}
						y1={0}
						y2={height}
						stroke="white"
						stroke-dasharray="4"
						stroke-width="1"
					/>
				{/if}
			{:else}
				{#each groupedStack as layer}
					<path
						d={area<any>()
							.x((d, i) => xScale(+groupedData[i].year))
							.y0((d) => yScale(d[0]))
							.y1((d) => yScale(d[1]))(layer)}
						fill={color(layer.key)}
						stroke="whitenormal "
						stroke-width="0.5"
					/>
				{/each}

				{#each [{ name: 'Noncommunicable diseases', index: 0 }, { name: 'Communicable diseases', index: 1 }, { name: 'Injuries', index: 2 }] as { name, index }}
					<text
						x={20}
						y={yScale(groupedStack[index]?.[0]?.[1] ?? 0) + 25}
						text-anchor="start"
						fill="white"
						class="subtitle-2"
					>
						{copy.labels[name]}
					</text>
				{/each}
			{/if}

			<g transform="translate(0, {height})">
				{#each xScale.ticks(10) as t (t)}
					<g transform="translate({xScale(t)}, 0)">
						<text y="20" text-anchor="middle" class="microcopy-2">{t}</text>
					</g>
				{/each}
				<text
					x={(width - margin.left - margin.right) / 2}
					y="50"
					text-anchor="middle"
					class="over-line-3">{copy.x_axis}</text
				>
			</g>

			<g>
				{#each yScale.ticks(10) as t (t)}
					<g transform="translate(0, {yScale(t)})">
						<text x="-10" dy="0.32em" text-anchor="end" class="microcopy-2 font-bold">
							{Math.round(t * 100)}%
						</text>
					</g>
				{/each}
				<text
					transform="rotate(-90)"
					x={-height / 2}
					y="-60"
					text-anchor="middle"
					class="over-line-3"
				>
					{copy.y_axis}
				</text>
			</g>
		</g>
	</svg>

	{#if hovered && tooltip}
		<Tooltip>
			<div>
				<b>{tooltip.name}</b>
				{copy.tooltip_fragments[0]}
				<emph>{tooltip.value}%</emph>
				{copy.tooltip_fragments[1]}
				{tooltip.year}
			</div>
		</Tooltip>
	{/if}
</div>

<style lang="postcss">
	@reference '$lib/styles/app.css';

	emph {
		@apply font-bold;
	}
</style>
